import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell, Search, UsersRound, Gauge, CircleDollarSign,
  Medal, Ticket, FileText, Eye, Plus, History,
  CalendarClock, CheckCircle2, Upload,
} from "lucide-react";
import AdminSidebar from "../../components/admin/AdminSidebar";
import StatCard from "../../components/admin/StatCard";
import { adminApi, poolsApi, ticketRequestsApi } from "../../services/api";
import { SkeletonStat, SkeletonRow } from "../../components/shared/Skeleton";

function fmt(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);
}
function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
}

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [pools, setPools] = useState([]);
  const [requests, setRequests] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [statsResult, poolsResult, logsResult, requestResult] = await Promise.allSettled([
        adminApi.getStats(),
        poolsApi.getAll({ status: "filling", limit: 5 }),
        adminApi.getLogs({ limit: 4 }),
        ticketRequestsApi.getAll({ limit: 6 }),
      ]);

      if (statsResult.status === "fulfilled") setStats(statsResult.value.data.data);
      if (poolsResult.status === "fulfilled") setPools(poolsResult.value.data.data || []);
      if (logsResult.status === "fulfilled") setLogs(logsResult.value.data.data || []);
      if (requestResult.status === "fulfilled") setRequests(requestResult.value.data.data || []);
      setLoading(false);
    }
    load();
  }, []);

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Overview</div>
          <label className="admin-search"><Search size={15} /><input placeholder="Search across command center..." /></label>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content">
          {loading ? (
            <>
              <section className="stats-grid">
                <SkeletonStat /><SkeletonStat /><SkeletonStat /><SkeletonStat />
              </section>
              <section className="admin-grid">
                <article className="admin-panel pools-panel">
                  <div className="panel-heading"><h2><Ticket size={20} /> Active Pools</h2></div>
                  <div className="data-table pool-table">
                    <div className="table-head"><span>Pool ID</span><span>Draw Date</span><span>Member Load</span><span>Status</span><span>Actions</span></div>
                    {Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={5} />)}
                  </div>
                </article>
                <aside className="admin-side-stack">
                  <article className="admin-panel logs-panel" style={{ minHeight: 200 }} />
                  <article className="admin-panel draws-panel" style={{ minHeight: 120 }} />
                </aside>
              </section>
            </>
          ) : (
            <>
              <section className="stats-grid">
                <StatCard icon={UsersRound} label="Total Users" value={stats?.totalUsers?.toLocaleString() ?? "—"} tone="amber" />
                <StatCard icon={Gauge} label="Active Pools" value={stats?.activePools ?? "—"} tone="blue" />
                <StatCard icon={CircleDollarSign} label="Total Revenue" value={fmt(stats?.totalRevenue)} tone="yellow" />
                <StatCard icon={Medal} label="Ticket Requests" value={requests.length} tone="green" />
              </section>

              <section className="admin-grid">
                <article className="admin-panel pools-panel">
                  <div className="panel-heading">
                    <h2><Ticket size={20} /> Active Pools</h2>
                    <Link to="/admin/pools" style={{ background: "rgba(255,211,119,.13)", color: "var(--gold)", border: 0, borderRadius: 6, padding: "6px 10px", fontSize: 13, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Plus size={14} /> New Pool
                    </Link>
                  </div>
                  {pools.length === 0 ? (
                    <div style={{ padding: 32, color: "var(--muted)", textAlign: "center", fontSize: 14 }}>No active pools</div>
                  ) : (
                    <div className="data-table pool-table">
                      <div className="table-head"><span>Pool ID</span><span>Draw Date</span><span>Member Load</span><span>Status</span><span>Actions</span></div>
                      {pools.map((p) => {
                        const pct = p.size ? Math.min((p.memberCount / p.size) * 100, 100) : 0;
                        return (
                          <div className="table-row" key={p._id}>
                            <strong>{p.poolId}</strong>
                            <span>{fmtDate(p.drawDate)}<small>Powerball</small></span>
                            <span>{p.memberCount} / {p.size}<small>{pct.toFixed(1)}%</small><i className="mini-progress"><b style={{ width: `${pct}%` }} /></i></span>
                            <em className={p.status === "locked" ? "locked" : ""}>{p.status}</em>
                            <span className="row-actions">
                              <Link to="/admin/pools"><button aria-label="Manage pools"><FileText size={15} /></button></Link>
                              <button aria-label="View"><Eye size={15} /></button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </article>

                <aside className="admin-side-stack">
                  <article className="admin-panel logs-panel">
                    <div className="panel-heading"><h2><FileText size={17} /> Purchase Requests</h2><Link to="/admin/tickets">Manage</Link></div>
                    {requests.length === 0 ? (
                      <div style={{ padding: 20, color: "var(--muted)", fontSize: 13 }}>No ticket requests waiting.</div>
                    ) : requests.slice(0, 4).map((request) => (
                      <div className="log-row" key={request._id}>
                        <code className={request.status === "paid" ? "sys" : "inf"}>{request.quantity}x</code>
                        <p>{request.user?.name || "User"} - {request.pool?.poolId || "Pool"}<small>{request.status?.replaceAll("_", " ")}</small></p>
                      </div>
                    ))}
                  </article>
                  <article className="admin-panel logs-panel">
                    <div className="panel-heading"><h2><History size={17} /> Activity Logs</h2><span>Live</span></div>
                    {logs.length === 0 ? (
                      <div style={{ padding: 20, color: "var(--muted)", fontSize: 13 }}>No recent activity</div>
                    ) : logs.map((log) => (
                      <div className="log-row" key={log._id}>
                        <code className="inf">LOG</code>
                        <p>{log.action} — {log.entity}<small>{new Date(log.createdAt).toLocaleTimeString()}</small></p>
                      </div>
                    ))}
                  </article>
                  <article className="admin-panel draws-panel">
                    <div className="panel-heading"><h2><CalendarClock size={17} /> Upcoming Draws</h2></div>
                    {pools.slice(0, 2).map((p, i) => (
                      <div key={p._id} className={`draw-row ${i === 0 ? "featured" : ""}`}>
                        <div><strong>{fmtDate(p.drawDate)}</strong><span>{p.name}</span></div>
                        <b>{p.poolId}</b>
                      </div>
                    ))}
                  </article>
                </aside>
              </section>

              <section className="admin-panel command-panel">
                <h2>Direct Commands</h2>
                <div className="command-grid">
                  <Link to="/admin/pools" className="command-grid-btn"><Plus size={20} /> New Pool</Link>
                  <Link to="/admin/tickets" className="command-grid-btn"><Upload size={20} /> Assign Tickets</Link>
                  <Link to="/admin/results" className="command-grid-btn"><CheckCircle2 size={20} /> Enter Results</Link>
                  <Link to="/admin/users" className="command-grid-btn"><UsersRound size={20} /> Manage Users</Link>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
